import {
  GameCardPrintingsQuery,
  PrintingOptionsResponse,
  PrintingsPagedResponse,
  PrintingsSortEnum,
} from "modules/cards/model/card";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { hashKey, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { API_ROUTES } from "utils/api-route.constants";
import { buildCardPrintingsUrl } from "modules/cards/utils/cardReadUrls";
import {
  INFINITE_STATIC_DATA_CONFIG,
  STATIC_DATA_CONFIG,
} from "modules/common/react-query-configs";
import {
  usePricingRequest,
  withPricingCacheKey,
} from "modules/common/hooks/usePricingRequest";

export function useInfiniteCardPrintings(
  cardId: string,
  perPage = 20,
  sort?: PrintingsSortEnum,
  order?: SortOrderEnum,
  includeSelf = false
) {
  const fetch = useFetchWrapper();
  const { pricing, cacheKey, isAuthReady } = usePricingRequest();

  return useInfiniteQuery<PrintingsPagedResponse>({
    queryKey: withPricingCacheKey(
      [
        QUERIES.MAGIC_CARDS_PRINTINGS,
        "infinite",
        cardId,
        perPage,
        sort,
        order,
        includeSelf,
      ],
      cacheKey
    ),
    queryFn: ({ pageParam }) => {
      if (!pricing) {
        throw new Error("Pricing is required for card printings");
      }
      const req: GameCardPrintingsQuery = {
        perPage,
        page: pageParam as number,
        ...(sort && { sort }),
        ...(order && { order }),
        ...(includeSelf && { includeSelf }),
      };
      return fetch.publicGet(
        buildCardPrintingsUrl(cardId, { ...req, ...pricing }),
        null
      );
    },
    enabled: isAuthReady && !!pricing && !!cardId,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    ...INFINITE_STATIC_DATA_CONFIG,
    // Sort/order live in the key, so a sort change is a brand-new query: hold
    // the previous printings for the SAME card (index 2 of the key) so the list
    // animates to the new order instead of collapsing to a skeleton.
    //
    // Scoped to the pricing key (the tail withPricingCacheKey appends) for the
    // same reason as useInfiniteCards: a cacheKey change (anonymous →
    // signed-in) re-keys the query too, and those rows carry the PREVIOUS
    // configuration's prices. Compared by hash because the key is rebuilt fresh
    // on every render.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === cardId &&
      hashKey([previousQuery?.queryKey.at(-1)]) === hashKey([cacheKey])
        ? previous
        : undefined,
  });
}

export function useCardPrintings(cardId: string) {
  const fetch = useFetchWrapper();
  const { pricing, cacheKey, isAuthReady } = usePricingRequest();

  const query = useQuery<PrintingsPagedResponse>({
    queryKey: withPricingCacheKey(
      [QUERIES.MAGIC_CARDS_PRINTINGS, cardId],
      cacheKey
    ),
    queryFn: () => {
      if (!pricing) {
        throw new Error("Pricing is required for card printings");
      }
      const req: GameCardPrintingsQuery = {
        perPage: 100,
        page: 1,
      };
      return fetch.publicGet(
        buildCardPrintingsUrl(cardId, { ...req, ...pricing }),
        null
      );
    },
    enabled: isAuthReady && !!pricing && !!cardId,
    ...STATIC_DATA_CONFIG,
  });

  return {
    printings: query.data?.cards ?? [],
    isFetching: query.isFetching,
  };
}

export function useCardPrintingOptions(cardId: string) {
  const fetch = useFetchWrapper();

  return useQuery<PrintingOptionsResponse>({
    queryKey: [QUERIES.MAGIC_CARDS_PRINTINGS, "options", cardId],
    queryFn: () =>
      fetch.publicGet(API_ROUTES.MAGIC_CARDS.PRINTING_OPTIONS(cardId), null),
    enabled: !!cardId,
    ...STATIC_DATA_CONFIG,
  });
}
